import React, { createContext, useContext, useState, useEffect } from "react";
import { authAPI } from "../utils/api";

const AuthContext = createContext();

// Role permissions mapping (mirrors backend)
const ROLE_PERMISSIONS = {
  super_admin: {
    manage_users: true,
    publish_articles: true,
    create_articles: true,
    manage_pseo: true,
    view_dashboard: true,
    manage_authors: true
  },
  admin: {
    manage_users: false,
    publish_articles: true,
    create_articles: true,
    manage_pseo: true,
    view_dashboard: true,
    manage_authors: true
  },
  editor: {
    manage_users: false,
    publish_articles: true,
    create_articles: true,
    manage_pseo: true,
    view_dashboard: true,
    manage_authors: true
  },
  author: {
    manage_users: false,
    publish_articles: false,
    create_articles: true,
    manage_pseo: false,
    view_dashboard: true,
    manage_authors: false
  }
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const verifyToken = async () => {
      const token = localStorage.getItem("token");
      if (token) {
        try {
          const response = await authAPI.verify();
          setUser(response.data);
          setError(null);
        } catch (err) {
          console.error("Token verification failed:", err);
          localStorage.removeItem("token");
          setError(err.message);
        }
      }
      setLoading(false);
    };
    verifyToken();
  }, []);

  const login = async (email, password) => {
    try {
      const response = await authAPI.login({ email, password });
      const { access_token, user: userData } = response.data;

      // Store token with Bearer prefix for consistency
      localStorage.setItem("token", access_token);
      setUser(userData);
      setError(null);

      return response.data;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  const register = async (email, password, name) => {
    try {
      const response = await authAPI.register({ email, password, name });
      const { access_token, user: userData } = response.data;

      // Store token with Bearer prefix for consistency
      localStorage.setItem("token", access_token);
      setUser(userData);
      setError(null);

      return response.data;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  const logout = () => {
    localStorage.removeItem("token");
    setUser(null);
    setError(null);
  };

  // Check if user has a specific permission
  const hasPermission = (permission) => {
    if (!user) return false;
    const userRole = user.role || 'super_admin'; // Default to super_admin if role missing
    const permissions = ROLE_PERMISSIONS[userRole] || ROLE_PERMISSIONS.super_admin;
    return permissions[permission] || false;
  };

  // Check if user has one of the allowed roles
  const hasRole = (...roles) => {
    if (!user) return false;
    const userRole = user.role || 'super_admin';
    return roles.includes(userRole);
  };

  const value = {
    user,
    loading,
    error,
    login,
    register,
    logout,
    isAuthenticated: !!user,
    hasPermission,
    hasRole,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
